import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Modal,
  Pressable,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
  Image as RNImage,
  Alert,
} from 'react-native';
import { Image } from 'expo-image';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../context/ThemeContext';
import CourseMediaCarousel, { resolveRemoteUri } from '../components/CourseMediaCarousel';
import ModalVideoEmbed from '../components/ModalVideoEmbed';
import { ExternalBreaksCarousel, getExternalBreaksCarouselStylesBundle } from '../screens/MyCourseDetailPage';
import { getApiUrl } from '../utils/apiConfig';
import { resolveCourseGalleryEntryToUrl } from '../utils/courseMediaUrls';
import { uploadHandbookCommentImageFromPicker, uploadHandbookCommentYoutubeFromPicker } from '../utils/handbookCommentMediaUpload';

const CAROUSEL_WIDTH_RATIO = 0.6;
const CAROUSEL_ASPECT = 16 / 9;
const TICK_GREEN = '#2e7d32';
/** Delete confirm modal — same fills in light/dark; one TouchableOpacity per choice (nested Pressable/View breaks native layout). */
const DELETE_MODAL_BTN_NO_BG = '#B0B0B0';
const DELETE_MODAL_BTN_YES_BG = '#d4af37';

function normalizeUrlList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((u) => resolveCourseGalleryEntryToUrl(String(u ?? '').trim())).filter(Boolean);
}

function authorDisplayName(c) {
  return (
    String(c.author_student_name || '').trim() ||
    String(c.author_username || '').trim() ||
    `Student ${c.author_student_id ?? ''}`
  );
}

/** @typedef {'all'|'moves'|'recent'|'tagged'} HandbookCommentListFilter */

const HANDBOOK_COMMENT_FILTER = {
  ALL: 'all',
  MOVES: 'moves',
  RECENT: 'recent',
  TAGGED: 'tagged',
};

const HANDBOOK_COMMENT_FILTER_OPTIONS = [
  { id: HANDBOOK_COMMENT_FILTER.ALL, label: 'All Comments' },
  { id: HANDBOOK_COMMENT_FILTER.MOVES, label: 'In Moves order' },
  { id: HANDBOOK_COMMENT_FILTER.RECENT, label: 'Most Recent' },
  { id: HANDBOOK_COMMENT_FILTER.TAGGED, label: "Comments I'm tagged in" },
];

/**
 * Apply handbook comment list filter/sort. Fetches still load full entry list; this shapes display order.
 * @param {object[]} rows
 * @param {HandbookCommentListFilter} mode
 * @param {number|string|null|undefined} userId
 */
function applyHandbookCommentFilter(rows, mode, userId) {
  const list = Array.isArray(rows) ? [...rows] : [];
  let filtered = list;
  if (mode === HANDBOOK_COMMENT_FILTER.TAGGED) {
    const uid = userId != null ? Number(userId) : NaN;
    if (!Number.isFinite(uid)) {
      filtered = [];
    } else {
      filtered = list.filter((c) => {
        const tags = Array.isArray(c.tagged_student_ids) ? c.tagged_student_ids : [];
        return tags.some((t) => Number(t) === uid);
      });
    }
  }

  const createdTs = (c) => {
    const t = c.created_at ? new Date(c.created_at).getTime() : 0;
    return Number.isFinite(t) ? t : 0;
  };

  if (mode === HANDBOOK_COMMENT_FILTER.RECENT || mode === HANDBOOK_COMMENT_FILTER.TAGGED) {
    filtered.sort((a, b) => createdTs(b) - createdTs(a));
  } else if (mode === HANDBOOK_COMMENT_FILTER.ALL || mode === HANDBOOK_COMMENT_FILTER.MOVES) {
    // ALL: match API chronological order (oldest first). MOVES: placeholder — same until per-move ordering exists.
    filtered.sort((a, b) => createdTs(a) - createdTs(b));
  }
  return filtered;
}

function commentApiBase({ entryId, studentNoteId }) {
  if (studentNoteId != null) {
    return `${getApiUrl()}/api/student-notes/${encodeURIComponent(String(studentNoteId))}/comments`;
  }
  return `${getApiUrl()}/api/handbook-entries/${encodeURIComponent(String(entryId))}/comments`;
}

function commentResourceUrl({ entryId, studentNoteId, commentId, suffix = '' }) {
  const base = commentApiBase({ entryId, studentNoteId });
  if (commentId == null) return base;
  return `${base}/${encodeURIComponent(String(commentId))}${suffix}`;
}

export default function HandbookEntryCommentsList({
  entryId,
  studentNoteId,
  user,
  formTitle = '',
  noteTitle = '',
  refreshKey = 0,
  focusCommentId = null,
  onViewRecorded,
  onCommentsMutated,
}) {
  const isStudentNote = studentNoteId != null;
  const parentId = isStudentNote ? studentNoteId : entryId;
  const displayTitle = isStudentNote ? noteTitle : formTitle;
  const theme = useTheme();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editCommentId, setEditCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editImageUrls, setEditImageUrls] = useState([]);
  const [editVideoUrls, setEditVideoUrls] = useState([]);
  const [editPendingImage, setEditPendingImage] = useState(null);
  const [editPendingVideo, setEditPendingVideo] = useState(null);
  const [editVideoTitle, setEditVideoTitle] = useState('');
  const [editFileBase64, setEditFileBase64] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [videoModal, setVideoModal] = useState(null);
  const [imageLightbox, setImageLightbox] = useState(null);
  const [rotateDeg, setRotateDeg] = useState(0);
  const [viewersForComment, setViewersForComment] = useState(null);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [commentListFilter, setCommentListFilter] = useState(() => HANDBOOK_COMMENT_FILTER.ALL);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const viewMarkedRef = useRef(null);

  /** "Tagged" filter requires a logged-in user; otherwise the list is empty — reset to All. */
  useEffect(() => {
    if (user?.id == null && commentListFilter === HANDBOOK_COMMENT_FILTER.TAGGED) {
      setCommentListFilter(HANDBOOK_COMMENT_FILTER.ALL);
    }
  }, [user?.id, commentListFilter]);

  const slideWidth = Math.max(1, Math.round(windowWidth * CAROUSEL_WIDTH_RATIO));
  const slideHeight = Math.round(slideWidth / CAROUSEL_ASPECT);
  const breaksStyles = useMemo(
    () => getExternalBreaksCarouselStylesBundle(theme, slideWidth),
    [theme, slideWidth]
  );
  const carouselStyles = useMemo(
    () => ({
      ...breaksStyles,
      extSection: { ...breaksStyles.extSection, marginBottom: 8, marginTop: 4 },
    }),
    [breaksStyles]
  );

  const lightboxMax = useMemo(() => {
    const horizontalPad = 18;
    const topReserve = 54;
    const bottomReserve = 72;
    const maxW = Math.max(80, windowWidth - horizontalPad * 2);
    const maxH = Math.max(80, windowHeight - topReserve - bottomReserve);
    return { w: maxW, h: maxH };
  }, [windowWidth, windowHeight]);

  const listStyles = useMemo(() => getListStyles(theme), [theme]);

  const displayedComments = useMemo(
    () => applyHandbookCommentFilter(comments, commentListFilter, user?.id),
    [comments, commentListFilter, user?.id]
  );

  const selectedFilterLabel = useMemo(() => {
    const opt = HANDBOOK_COMMENT_FILTER_OPTIONS.find((o) => o.id === commentListFilter);
    return opt?.label ?? 'All Comments';
  }, [commentListFilter]);

  const filterOptions = useMemo(
    () =>
      isStudentNote
        ? HANDBOOK_COMMENT_FILTER_OPTIONS.filter((o) => o.id !== HANDBOOK_COMMENT_FILTER.MOVES)
        : HANDBOOK_COMMENT_FILTER_OPTIONS,
    [isStudentNote]
  );

  const loadComments = useCallback(async () => {
    if (parentId == null) return;
    setLoading(true);
    try {
      // Avoid stale lists on web after DELETE: cache-bust query + no-store (do not add
      // Cache-Control/Pragma *request* headers — those trigger CORS preflight on GET and can fail).
      const bust = `_=${Date.now()}`;
      const viewerQ =
        user?.id != null ? `&viewer_student_id=${encodeURIComponent(String(user.id))}` : '';
      const r = await fetch(`${commentApiBase({ entryId, studentNoteId })}?${bust}${viewerQ}`, {
        cache: 'no-store',
      });
      if (!r.ok) throw new Error(String(r.status));
      const rows = await r.json();
      setComments(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.warn('[Handbook comments] loadComments failed', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [parentId, entryId, studentNoteId, user?.id]);

  useEffect(() => {
    loadComments();
  }, [loadComments, refreshKey]);

  const markViewIfNeeded = useCallback(async () => {
    const cid = focusCommentId != null ? parseInt(String(focusCommentId), 10) : NaN;
    if (!Number.isFinite(cid) || !user?.id) return;
    const key = `${cid}:${user.id}`;
    if (viewMarkedRef.current === key) return;
    try {
      const res = await fetch(`${getApiUrl()}/api/comments/${encodeURIComponent(String(cid))}/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ viewer_student_id: user.id }),
      });
      viewMarkedRef.current = key;
      if (res.ok) {
        onViewRecorded?.();
        await loadComments();
      }
    } catch {
      viewMarkedRef.current = key;
    }
  }, [focusCommentId, user?.id, loadComments, onViewRecorded]);

  useEffect(() => {
    markViewIfNeeded();
  }, [markViewIfNeeded]);

  useEffect(() => {
    if (!imageLightbox) setRotateDeg(0);
  }, [imageLightbox]);

  const openVideoModal = useCallback((uri, title = 'Video', options = {}) => {
    if (!uri) return;
    setVideoModal({
      uri,
      title: title || 'Video',
      isImage: !!options.isImage,
    });
  }, []);

  const closeVideoModal = useCallback(() => setVideoModal(null), []);

  const openViewersModal = async (comment) => {
    const ids = Array.isArray(comment.viewed_by_student_ids) ? comment.viewed_by_student_ids : [];
    if (!ids.length) {
      setViewersForComment({ commentId: comment.id, rows: [] });
      return;
    }
    setViewersLoading(true);
    try {
      const r = await fetch(`${getApiUrl()}/api/students/brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const rows = r.ok ? await r.json() : [];
      setViewersForComment({ commentId: comment.id, rows: Array.isArray(rows) ? rows : [] });
    } catch {
      setViewersForComment({ commentId: comment.id, rows: [] });
    } finally {
      setViewersLoading(false);
    }
  };

  const startEdit = (comment) => {
    setEditCommentId(comment.id);
    setEditText(String(comment.description || ''));
    setEditImageUrls(
      Array.isArray(comment.image_links) ? comment.image_links.map((u) => String(u ?? '').trim()).filter(Boolean) : []
    );
    setEditVideoUrls(
      Array.isArray(comment.video_links) ? comment.video_links.map((u) => String(u ?? '').trim()).filter(Boolean) : []
    );
    setEditPendingImage(null);
    setEditPendingVideo(null);
    setEditVideoTitle('');
    setEditFileBase64(null);
  };

  const cancelEdit = () => {
    setEditCommentId(null);
    setEditText('');
    setEditImageUrls([]);
    setEditVideoUrls([]);
    setEditPendingImage(null);
    setEditPendingVideo(null);
    setEditVideoTitle('');
    setEditFileBase64(null);
  };

  const selectCommentFilter = async (mode) => {
    setCommentListFilter(mode);
    setFilterDropdownOpen(false);
    cancelEdit();
    await loadComments();
  };

  const saveEdit = async () => {
    if (parentId == null || editCommentId == null || !user?.id) return;
    if (editPendingVideo && !editVideoTitle.trim()) {
      Alert.alert('Video title', 'Enter a name for the video before saving.');
      return;
    }
    const hasAtt =
      editImageUrls.length > 0 ||
      editVideoUrls.length > 0 ||
      !!editPendingImage ||
      !!(editPendingVideo && editFileBase64);
    const t = editText.trim();
    if (!t && !hasAtt) {
      Alert.alert('Comment', 'Add text or keep at least one attachment.');
      return;
    }
    setEditSaving(true);
    try {
      let newImageUrls = [];
      let newVideoUrls = [];
      if (editPendingImage) {
        newImageUrls = await uploadHandbookCommentImageFromPicker(
          isStudentNote
            ? { noteId: parentId, pendingImage: editPendingImage }
            : { entryId: parentId, pendingImage: editPendingImage }
        );
      }
      if (editPendingVideo && editFileBase64) {
        newVideoUrls = await uploadHandbookCommentYoutubeFromPicker({
          fileBase64: editFileBase64,
          videoTitle: editVideoTitle,
          formTitle: displayTitle || 'Comment',
          contextLabel: isStudentNote ? 'Student note comment' : 'Handbook entry comment',
        });
      }

      const res = await fetch(
        commentResourceUrl({ entryId, studentNoteId, commentId: editCommentId }),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            author_student_id: user.id,
            description: t,
            new_image_urls: newImageUrls,
            new_video_urls: newVideoUrls,
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Save failed (${res.status})`);
      }
      cancelEdit();
      await loadComments();
      onCommentsMutated?.();
    } catch (e) {
      Alert.alert('Save failed', e.message || 'Could not update comment.');
    } finally {
      setEditSaving(false);
    }
  };

  const removeImageWhileEditing = async (publicUrl) => {
    if (parentId == null || editCommentId == null || !user?.id) return;
    setEditSaving(true);
    try {
      const res = await fetch(
        commentResourceUrl({ entryId, studentNoteId, commentId: editCommentId, suffix: '/remove-image' }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            author_student_id: user.id,
            public_url: publicUrl,
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Could not remove image (${res.status})`);
      }
      setEditImageUrls((prev) => prev.filter((u) => u !== publicUrl));
      await loadComments();
      onCommentsMutated?.();
    } catch (e) {
      Alert.alert('Remove image', e.message || 'Could not delete image.');
    } finally {
      setEditSaving(false);
    }
  };

  const removeVideoWhileEditing = async (videoUrl) => {
    if (parentId == null || editCommentId == null || !user?.id) return;
    setEditSaving(true);
    try {
      const res = await fetch(
        commentResourceUrl({ entryId, studentNoteId, commentId: editCommentId, suffix: '/remove-video' }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            author_student_id: user.id,
            video_url: videoUrl,
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Could not remove video (${res.status})`);
      }
      setEditVideoUrls((prev) => prev.filter((u) => u !== videoUrl));
      await loadComments();
      onCommentsMutated?.();
    } catch (e) {
      Alert.alert('Remove video', e.message || 'Could not delete video.');
    } finally {
      setEditSaving(false);
    }
  };

  const pickEditImage = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission', 'Allow photo library access to attach an image.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: false,
        quality: 0.92,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const a = result.assets[0];
      setEditPendingVideo(null);
      setEditFileBase64(null);
      setEditVideoTitle('');
      setEditPendingImage({
        uri: a.uri,
        fileName: a.fileName || a.uri.split(/[/\\]/).pop() || 'image.jpg',
        mimeType: a.mimeType ?? null,
      });
    } catch (e) {
      Alert.alert('Image', e.message || 'Could not pick image.');
    }
  };

  const pickEditVideo = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission', 'Allow library access to attach a video.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Videos,
        allowsMultipleSelection: false,
        quality: 1,
        base64: true,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const a = result.assets[0];
      setEditPendingImage(null);
      const fileName = a.fileName || a.uri.split(/[/\\]/).pop() || 'video.mp4';
      setEditVideoTitle('');
      let b64 = a.base64;
      if (!b64) {
        const response = await fetch(a.uri);
        const blob = await response.blob();
        b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const s = reader.result;
            if (typeof s === 'string' && s.includes(',')) resolve(s.split(',')[1]);
            else reject(new Error('Could not read file'));
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      setEditFileBase64(b64);
      setEditPendingVideo({ fileName });
    } catch (e) {
      Alert.alert('Video', e.message || 'Could not pick video.');
    }
  };

  const clearEditPendingImage = useCallback(() => setEditPendingImage(null), []);
  const clearEditPendingVideo = useCallback(() => {
    setEditPendingVideo(null);
    setEditFileBase64(null);
    setEditVideoTitle('');
  }, []);

  const executeDeleteComment = async (comment) => {
    if (parentId == null || !user?.id || !comment?.id) {
      console.warn('[Comment delete] early exit', {
        parentId,
        userId: user?.id,
        commentId: comment?.id,
      });
      return;
    }
    const base = commentResourceUrl({ entryId, studentNoteId, commentId: comment.id });
    const q = `author_student_id=${encodeURIComponent(String(user.id))}`;
    const url = `${base}?${q}`;
    console.log('[Handbook comment delete] request', {
      url,
      commentId: comment.id,
      author_student_id: user.id,
      typeofAuthorId: typeof user.id,
    });
    setEditSaving(true);
    try {
      // Query-only DELETE avoids dropped bodies (some proxies) and keeps CORS preflight simpler than JSON body.
      const res = await fetch(url, { method: 'DELETE', cache: 'no-store' });
      const body = await res.json().catch(() => ({}));
      console.log('[Handbook comment delete] response', {
        ok: res.ok,
        status: res.status,
        body,
      });
      if (!res.ok) {
        const code = body.code ? ` (${body.code})` : '';
        const det = body.detail ? `: ${body.detail}` : '';
        const phase = body.phase ? ` [${body.phase}]` : '';
        throw new Error((body.error || `Delete failed (${res.status})`) + phase + code + det);
      }
      console.log('[Handbook comment delete] success, reloading list');
      if (editCommentId === comment.id) cancelEdit();
      const deletedId = Number(comment.id);
      setComments((prev) => prev.filter((c) => Number(c.id) !== deletedId));
      await loadComments();
      onCommentsMutated?.();
    } catch (e) {
      console.error('[Handbook comment delete] error', e?.message || e);
      Alert.alert('Delete failed', e.message || 'Could not delete comment.');
    } finally {
      setEditSaving(false);
    }
  };

  const yellow = theme.colors.heading;

  if (parentId == null) return null;

  return (
    <View
      style={listStyles.outerBox}
      {...(Platform.OS === 'web' ? { dataSet: { handbookCommentsSection: 'true' } } : {})}
    >
      <TouchableOpacity
        style={listStyles.filterTrigger}
        onPress={() => setFilterDropdownOpen(true)}
        disabled={editSaving}
        accessibilityRole="button"
        accessibilityLabel="Comment list filter"
        accessibilityHint="Opens sort and filter options for comments"
        {...(Platform.OS === 'web' ? { nativeID: 'handbook-comments-filter-trigger' } : {})}
      >
        <View style={listStyles.filterTriggerInner}>
          <Text style={[listStyles.sectionHeading, listStyles.filterTriggerLabel, { color: yellow }]}>
            {selectedFilterLabel}
          </Text>
          <MaterialIcons name="arrow-drop-down" size={26} color={yellow} style={listStyles.filterTriggerIcon} />
        </View>
      </TouchableOpacity>

      {loading ? (
        <ActivityIndicator color={yellow} style={{ marginVertical: 16 }} />
      ) : !displayedComments.length ? (
        <Text style={[listStyles.emptyHint, { color: theme.colors.text }]}>
          There are no comments based on this filter.
        </Text>
      ) : (
        displayedComments.map((c, idx) => {
          const isLast = idx === displayedComments.length - 1;
          const imgUrls = normalizeUrlList(c.image_links);
          const vidUrls = normalizeUrlList(c.video_links);
          const viewed = Array.isArray(c.viewed_by_student_ids) ? c.viewed_by_student_ids : [];
          const breakLinks = vidUrls.map((url, i) => ({
            url,
            break_name: vidUrls.length > 1 ? `Video ${i + 1}` : 'Video',
          }));
          const pic = String(c.author_profile_picture_url || '').trim();

          return (
            <View
              key={String(c.id)}
              style={[
                listStyles.commentBlock,
                { borderColor: yellow },
                isLast && listStyles.commentBlockLast,
              ]}
            >
              <View style={listStyles.commentRow}>
                <View style={listStyles.authorRail}>
                  {pic ? (
                    <Image
                      source={{ uri: pic }}
                      style={listStyles.authorAvatar}
                      contentFit="cover"
                      transition={0}
                    />
                  ) : (
                    <View style={[listStyles.authorAvatar, listStyles.authorAvatarPlaceholder]}>
                      <MaterialIcons name="person" size={28} color={yellow} />
                    </View>
                  )}
                  <Text style={[listStyles.authorName, { color: theme.colors.text }]} numberOfLines={3}>
                    {authorDisplayName(c)}
                  </Text>
                </View>

                <View style={listStyles.bodyRail}>
                  <View style={listStyles.bodyAndIconsRow}>
                    <View style={listStyles.bodyContent}>
                      {editCommentId === c.id ? (
                        <TextInput
                          style={[
                            listStyles.editTextArea,
                            {
                              color: theme.colors.text,
                              borderColor: theme.colors.border,
                              backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fafafa',
                            },
                          ]}
                          value={editText}
                          onChangeText={setEditText}
                          placeholder="Comment text"
                          placeholderTextColor={theme.isDarkMode ? '#888' : '#666'}
                          multiline
                          textAlignVertical="top"
                          editable={!editSaving}
                        />
                      ) : (
                        <Text style={[listStyles.bodyText, { color: theme.colors.text }]}>{c.description}</Text>
                      )}

                      {editCommentId === c.id ? (
                        <>
                          {editImageUrls.map((rawUrl) => {
                            const display = resolveCourseGalleryEntryToUrl(rawUrl);
                            if (!display) return null;
                            return (
                              <View key={rawUrl} style={listStyles.editThumbWrap}>
                                <Image
                                  source={{ uri: display }}
                                  style={listStyles.editThumb}
                                  contentFit="cover"
                                  transition={0}
                                />
                                <TouchableOpacity
                                  style={listStyles.editThumbRemove}
                                  onPress={() => removeImageWhileEditing(rawUrl)}
                                  disabled={editSaving}
                                  accessibilityLabel="Remove image"
                                  accessibilityRole="button"
                                >
                                  <MaterialIcons name="close" size={18} color="#FFFFFF" />
                                </TouchableOpacity>
                              </View>
                            );
                          })}
                          {editVideoUrls.map((vurl) => (
                            <View key={vurl} style={listStyles.editMediaRow}>
                              <MaterialIcons name="videocam" size={22} color={yellow} />
                              <Text
                                style={[listStyles.editVideoLabel, { color: theme.colors.text }]}
                                numberOfLines={2}
                              >
                                {vurl}
                              </Text>
                              <TouchableOpacity
                                style={listStyles.editIconRemoveHit}
                                onPress={() => removeVideoWhileEditing(vurl)}
                                disabled={editSaving}
                                accessibilityLabel="Remove video"
                                accessibilityRole="button"
                              >
                                <MaterialIcons name="delete-outline" size={24} color="#c62828" />
                              </TouchableOpacity>
                            </View>
                          ))}

                          {editPendingVideo ? (
                            <TextInput
                              style={[
                                listStyles.editVideoTitleInput,
                                {
                                  color: theme.colors.text,
                                  borderColor: theme.colors.border,
                                  backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fafafa',
                                },
                              ]}
                              placeholder="Video name (required)"
                              placeholderTextColor={theme.isDarkMode ? '#888' : '#666'}
                              value={editVideoTitle}
                              onChangeText={setEditVideoTitle}
                              editable={!editSaving}
                            />
                          ) : null}

                          <View style={listStyles.editControlRow}>
                            <TouchableOpacity
                              style={[listStyles.editAttachIconBtn, { borderColor: yellow }]}
                              onPress={pickEditImage}
                              disabled={editSaving}
                              accessibilityLabel="Attach image"
                              accessibilityRole="button"
                            >
                              <MaterialIcons name="image" size={22} color={yellow} />
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[listStyles.editAttachIconBtn, { borderColor: yellow }]}
                              onPress={pickEditVideo}
                              disabled={editSaving}
                              accessibilityLabel="Attach video"
                              accessibilityRole="button"
                            >
                              <MaterialIcons name="videocam" size={22} color={yellow} />
                            </TouchableOpacity>
                          </View>

                          {editPendingImage ? (
                            <View style={listStyles.editPendingFileRow}>
                              <Text
                                style={[listStyles.editPendingFileName, { color: theme.colors.text }]}
                                numberOfLines={1}
                              >
                                {editPendingImage.fileName}
                              </Text>
                              <TouchableOpacity
                                onPress={clearEditPendingImage}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                              >
                                <Text style={[listStyles.editPendingRemove, { color: yellow }]}>✕</Text>
                              </TouchableOpacity>
                            </View>
                          ) : null}
                          {editPendingVideo ? (
                            <View style={listStyles.editPendingFileRow}>
                              <Text
                                style={[listStyles.editPendingFileName, { color: theme.colors.text }]}
                                numberOfLines={1}
                              >
                                {editPendingVideo.fileName}
                              </Text>
                              <TouchableOpacity
                                onPress={clearEditPendingVideo}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                              >
                                <Text style={[listStyles.editPendingRemove, { color: yellow }]}>✕</Text>
                              </TouchableOpacity>
                            </View>
                          ) : null}

                          <View style={listStyles.editFooterBtns}>
                            <TouchableOpacity
                              style={[listStyles.editSecondaryBtn, { borderColor: yellow }]}
                              onPress={cancelEdit}
                              disabled={editSaving}
                            >
                              <Text style={[listStyles.editSecondaryBtnText, { color: theme.colors.text }]}>
                                Cancel
                              </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              nativeID="handbook-comment-save-btn"
                              style={[listStyles.editPrimaryBtn, { backgroundColor: yellow }]}
                              onPress={saveEdit}
                              disabled={editSaving}
                            >
                              {editSaving ? (
                                <ActivityIndicator color="#000000" size="small" />
                              ) : (
                                <Text style={listStyles.editPrimaryBtnText}>Save</Text>
                              )}
                            </TouchableOpacity>
                          </View>
                        </>
                      ) : (
                        <>
                          {imgUrls.length > 0 ? (
                            <View style={listStyles.mediaBlock}>
                              <CourseMediaCarousel
                                mediaUrls={imgUrls}
                                slideWidth={slideWidth}
                                slideHeight={slideHeight}
                                theme={theme}
                                styles={breaksStyles}
                                onOpenVideo={(uri) => openVideoModal(uri, 'Video', { isImage: false })}
                                onPressImage={(url) => setImageLightbox({ kind: 'remote', uri: url })}
                                pauseAutoRotate={!!videoModal || !!imageLightbox}
                              />
                            </View>
                          ) : null}

                          {breakLinks.length > 0 ? (
                            <View style={listStyles.mediaBlock}>
                              <ExternalBreaksCarousel
                                links={breakLinks}
                                theme={theme}
                                styles={carouselStyles}
                                slideWidth={slideWidth}
                                slideHeight={slideHeight}
                                onOpenVideo={(uri, title, opts = {}) =>
                                  openVideoModal(uri, title, opts)
                                }
                                hideSectionHeading
                                compactSection
                              />
                            </View>
                          ) : null}
                        </>
                      )}

                      <View style={listStyles.seenRow}>
                        <TouchableOpacity
                          style={listStyles.eyeHit}
                          onPress={() => openViewersModal(c)}
                          accessibilityRole="button"
                          accessibilityLabel="Who has seen this comment"
                        >
                          <MaterialIcons name="visibility" size={22} color={theme.colors.text} />
                        </TouchableOpacity>
                        <View style={listStyles.ticksRow}>
                          {viewed.map((id) => (
                            <MaterialIcons key={`v-${c.id}-${id}`} name="check-circle" size={20} color={TICK_GREEN} />
                          ))}
                        </View>
                      </View>
                    </View>

                    {user?.id &&
                    Number(c.author_student_id) === Number(user.id) &&
                    editCommentId !== c.id ? (
                      <View style={listStyles.ownerIconCol}>
                        <TouchableOpacity
                          style={listStyles.iconBtnHit}
                          onPress={() => startEdit(c)}
                          disabled={editSaving}
                          accessibilityRole="button"
                          accessibilityLabel="Edit comment"
                        >
                          <MaterialIcons name="edit" size={22} color={yellow} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={listStyles.iconBtnHit}
                          onPress={() => setDeleteTarget(c)}
                          disabled={editSaving}
                          accessibilityRole="button"
                          accessibilityLabel="Delete comment"
                        >
                          <MaterialIcons name="delete-outline" size={22} color={yellow} />
                        </TouchableOpacity>
                      </View>
                    ) : null}
                  </View>
                </View>
              </View>
            </View>
          );
        })
      )}

      <Modal
        visible={deleteTarget != null}
        transparent
        animationType="fade"
        onRequestClose={() => setDeleteTarget(null)}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View
          style={listStyles.deleteModalRoot}
          {...(Platform.OS === 'web' ? { nativeID: 'handbook-comment-delete-modal-root' } : {})}
        >
          <Pressable style={listStyles.deleteModalBackdrop} onPress={() => setDeleteTarget(null)} />
          <View
            style={[
              listStyles.deleteConfirmSheet,
              {
                backgroundColor: theme.isDarkMode ? '#000000' : '#FFFFFF',
                borderColor: yellow,
                borderWidth: 2,
              },
            ]}
          >
            <Text style={listStyles.deleteConfirmTitle}>
              Are you sure you want to delete this comment?
            </Text>
            <View style={listStyles.deleteConfirmBtns}>
              <TouchableOpacity
                activeOpacity={0.88}
                nativeID="handbook-comment-delete-no-btn"
                style={[listStyles.deleteModalBtn, { backgroundColor: DELETE_MODAL_BTN_NO_BG }]}
                onPress={() => setDeleteTarget(null)}
                accessibilityRole="button"
                accessibilityLabel="No, keep comment"
              >
                <Text style={listStyles.deleteNoBtnText}>No</Text>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.88}
                nativeID="handbook-comment-delete-yes-btn"
                style={[listStyles.deleteModalBtn, { backgroundColor: DELETE_MODAL_BTN_YES_BG }]}
                onPress={async () => {
                  const t = deleteTarget;
                  setDeleteTarget(null);
                  if (t) await executeDeleteComment(t);
                }}
                accessibilityRole="button"
                accessibilityLabel="Yes, delete comment"
              >
                <Text style={listStyles.deleteYesBtnText}>Yes</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!videoModal}
        animationType="fade"
        transparent
        onRequestClose={closeVideoModal}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View style={listStyles.ytModalRoot}>
          <Pressable style={listStyles.ytModalBackdrop} onPress={closeVideoModal} />
          <View
            style={[
              listStyles.ytModalSheet,
              {
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#111',
                borderColor: theme.isDarkMode ? theme.colors.border : '#333',
              },
            ]}
          >
            <View style={listStyles.ytModalHeader}>
              <Text style={[listStyles.ytModalTitle, { color: theme.colors.heading }]} numberOfLines={2}>
                {videoModal?.title ?? 'Video'}
              </Text>
              <TouchableOpacity onPress={closeVideoModal} style={listStyles.ytModalClose}>
                <Text style={[listStyles.ytModalCloseText, { color: theme.colors.text }]}>✕</Text>
              </TouchableOpacity>
            </View>
            <View style={listStyles.ytWebViewWrap}>
              {videoModal?.uri ? (
                videoModal.isImage ? (
                  Platform.OS === 'web' ? (
                    <RNImage
                      source={{ uri: videoModal.uri }}
                      style={listStyles.ytModalImage}
                      resizeMode="contain"
                    />
                  ) : (
                    <Image
                      source={{ uri: videoModal.uri }}
                      style={listStyles.ytModalImage}
                      contentFit="contain"
                      transition={0}
                    />
                  )
                ) : (
                  <ModalVideoEmbed
                    uri={videoModal.uri}
                    title={videoModal.title}
                    webViewStyle={listStyles.ytWebView}
                  />
                )
              ) : null}
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!imageLightbox}
        transparent
        animationType="fade"
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={() => setImageLightbox(null)}
      >
        <View style={listStyles.lightboxRoot}>
          <TouchableOpacity
            style={StyleSheet.absoluteFillObject}
            activeOpacity={1}
            onPress={() => setImageLightbox(null)}
          />
          <View style={listStyles.lightboxSheet} pointerEvents="box-none">
            <TouchableOpacity
              style={listStyles.lightboxClose}
              onPress={() => setImageLightbox(null)}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            >
              <Text style={listStyles.lightboxCloseText}>✕</Text>
            </TouchableOpacity>
            <View style={{ transform: [{ rotate: `${rotateDeg}deg` }] }}>
              {imageLightbox?.kind === 'remote' ? (
                <Image
                  source={{ uri: resolveRemoteUri(imageLightbox.uri) }}
                  style={{
                    width: lightboxMax.w,
                    height: lightboxMax.h,
                  }}
                  contentFit="contain"
                />
              ) : null}
            </View>
            <View style={listStyles.lightboxRotateRow}>
              <TouchableOpacity
                style={listStyles.rotateHit}
                onPress={() => setRotateDeg((d) => (d - 90 + 360) % 360)}
              >
                <MaterialIcons name="rotate-left" size={26} color={yellow} />
              </TouchableOpacity>
              <TouchableOpacity
                style={listStyles.rotateHit}
                onPress={() => setRotateDeg((d) => (d + 90) % 360)}
              >
                <MaterialIcons name="rotate-right" size={26} color={yellow} />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={viewersForComment != null}
        transparent
        animationType="fade"
        onRequestClose={() => setViewersForComment(null)}
      >
        <View style={listStyles.tagModalRoot}>
          <Pressable style={listStyles.modalBackdrop} onPress={() => setViewersForComment(null)} />
          <View style={[listStyles.viewersSheet, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <Text style={[listStyles.viewersTitle, { color: yellow }]}>Viewed by</Text>
            {viewersLoading ? (
              <ActivityIndicator color={yellow} style={{ marginVertical: 12 }} />
            ) : (
              <ScrollView style={{ maxHeight: 280 }} keyboardShouldPersistTaps="handled">
                {(viewersForComment?.rows ?? []).length === 0 ? (
                  <Text style={{ color: theme.colors.text, opacity: 0.8 }}>No views recorded yet.</Text>
                ) : (
                  viewersForComment.rows.map((row) => (
                    <Text key={row.id} style={[listStyles.viewerLine, { color: theme.colors.text }]}>
                      {String(row.student_name || '').trim() || row.username || `Student ${row.id}`}
                    </Text>
                  ))
                )}
              </ScrollView>
            )}
            <TouchableOpacity
              style={[listStyles.modalDone, { backgroundColor: yellow }]}
              onPress={() => setViewersForComment(null)}
            >
              <Text style={listStyles.modalDoneText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={filterDropdownOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setFilterDropdownOpen(false)}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View style={listStyles.tagModalRoot}>
          <Pressable style={listStyles.modalBackdrop} onPress={() => setFilterDropdownOpen(false)} />
          <View
            style={[
              listStyles.filterOptionsSheet,
              { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
            ]}
          >
            <Text style={[listStyles.filterOptionsTitle, { color: yellow }]}>View comments</Text>
            <ScrollView style={listStyles.filterOptionsScroll} keyboardShouldPersistTaps="handled">
              {filterOptions.map((opt) => {
                const selected = opt.id === commentListFilter;
                return (
                  <Pressable
                    key={opt.id}
                    style={({ pressed }) => [
                      listStyles.filterOptionRow,
                      {
                        backgroundColor: pressed
                          ? theme.isDarkMode
                            ? 'rgba(255,255,255,0.06)'
                            : 'rgba(0,0,0,0.04)'
                          : 'transparent',
                      },
                    ]}
                    onPress={() => selectCommentFilter(opt.id)}
                  >
                    <Text style={[listStyles.filterOptionLabel, { color: theme.colors.text }]}>{opt.label}</Text>
                    {selected ? <MaterialIcons name="check" size={22} color={yellow} /> : <View style={{ width: 22 }} />}
                  </Pressable>
                );
              })}
            </ScrollView>
            <TouchableOpacity
              style={[listStyles.modalDone, { backgroundColor: yellow }]}
              onPress={() => setFilterDropdownOpen(false)}
            >
              <Text style={listStyles.modalDoneText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function getListStyles(theme) {
  return StyleSheet.create({
    outerBox: {
      marginTop: 12,
      alignSelf: 'stretch',
      width: '100%',
      maxWidth: '100%',
      paddingHorizontal: 0,
      paddingVertical: 0,
    },
    sectionHeading: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    filterTrigger: {
      marginBottom: 10,
      alignSelf: 'stretch',
      width: '100%',
      maxWidth: '100%',
      backgroundColor: 'transparent',
    },
    filterTriggerInner: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 2,
      maxWidth: '100%',
    },
    filterTriggerLabel: {
      marginBottom: 0,
      flexShrink: 1,
      backgroundColor: 'transparent',
    },
    filterTriggerIcon: {
      marginLeft: -2,
      marginTop: 2,
    },
    emptyHint: {
      fontSize: 14,
      opacity: 0.72,
      fontStyle: 'italic',
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    commentBlock: {
      alignSelf: 'stretch',
      borderWidth: 2,
      borderRadius: 10,
      padding: 12,
      marginBottom: 14,
    },
    commentBlockLast: {
      marginBottom: 0,
    },
    commentRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      alignSelf: 'stretch',
    },
    authorRail: {
      width: '15%',
      minWidth: 56,
      maxWidth: 96,
      alignItems: 'center',
      paddingRight: 8,
    },
    authorAvatar: {
      width: 52,
      height: 52,
      borderRadius: 26,
      overflow: 'hidden',
      marginBottom: 6,
    },
    authorAvatarPlaceholder: {
      borderWidth: 1,
      borderColor: theme.colors.heading,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#f5f5f5',
    },
    authorName: {
      fontSize: 11,
      lineHeight: 14,
      textAlign: 'center',
      width: '100%',
    },
    bodyRail: {
      flex: 1,
      minWidth: 0,
    },
    bodyAndIconsRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      gap: 4,
    },
    bodyContent: {
      flex: 1,
      minWidth: 0,
    },
    ownerIconCol: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: 10,
      paddingTop: 2,
      marginLeft: 4,
    },
    iconBtnHit: {
      padding: 6,
    },
    editTextArea: {
      minHeight: 88,
      borderWidth: 1,
      borderRadius: 8,
      padding: 12,
      fontSize: 15,
      lineHeight: 22,
      marginBottom: 10,
    },
    editMediaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 10,
      gap: 10,
    },
    editThumbWrap: {
      position: 'relative',
      alignSelf: 'flex-start',
      marginBottom: 10,
    },
    editThumb: {
      width: 96,
      height: 72,
      borderRadius: 8,
      backgroundColor: '#333',
    },
    editThumbRemove: {
      position: 'absolute',
      top: 4,
      right: 4,
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: 'rgba(0,0,0,0.72)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    editIconRemoveHit: {
      padding: 6,
    },
    editVideoTitleInput: {
      borderWidth: 1,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 15,
      marginBottom: 10,
    },
    editControlRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 10,
      marginTop: 4,
      marginBottom: 4,
    },
    editAttachIconBtn: {
      width: 44,
      height: 44,
      borderRadius: 8,
      borderWidth: 1.5,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    editPendingFileRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 8,
      paddingVertical: 2,
    },
    editPendingFileName: {
      flex: 1,
      fontSize: 13,
      marginRight: 8,
    },
    editPendingRemove: {
      fontSize: 18,
      fontWeight: '700',
      paddingHorizontal: 4,
    },
    editVideoLabel: {
      flex: 1,
      fontSize: 12,
      minWidth: 0,
    },
    editFooterBtns: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 12,
      marginTop: 8,
      marginBottom: 8,
    },
    editSecondaryBtn: {
      paddingVertical: 10,
      paddingHorizontal: 18,
      borderRadius: 8,
      borderWidth: 1.5,
    },
    editSecondaryBtnText: {
      fontSize: 15,
      fontWeight: '600',
    },
    editPrimaryBtn: {
      paddingVertical: 10,
      paddingHorizontal: 22,
      borderRadius: 8,
      minWidth: 88,
      alignItems: 'center',
      justifyContent: 'center',
    },
    editPrimaryBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#000000',
    },
    /** Full-screen modal shell: transparent so the app page shows through (only dimmed by backdrop). */
    deleteModalRoot: {
      flex: 1,
      justifyContent: 'center',
      paddingHorizontal: 20,
      width: '100%',
      height: '100%',
      backgroundColor: 'transparent',
    },
    deleteModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.55)',
    },
    deleteConfirmSheet: {
      borderRadius: 14,
      padding: 20,
      zIndex: 2,
      maxWidth: 400,
      alignSelf: 'center',
      width: '100%',
    },
    deleteConfirmTitle: {
      fontSize: 16,
      lineHeight: 22,
      marginBottom: 18,
      fontWeight: '600',
      color: theme.colors.text,
    },
    deleteConfirmBtns: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: 12,
    },
    deleteModalBtn: {
      flex: 1,
      minHeight: 48,
      paddingVertical: 14,
      paddingHorizontal: 16,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    deleteNoBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#FFFFFF',
    },
    deleteYesBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#FFFFFF',
    },
    bodyText: {
      fontSize: 15,
      lineHeight: 22,
      marginBottom: 10,
    },
    mediaBlock: {
      marginBottom: 10,
      alignSelf: 'stretch',
    },
    seenRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 8,
      flexWrap: 'wrap',
      gap: 6,
    },
    eyeHit: {
      padding: 4,
    },
    ticksRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 4,
    },
    ytModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    ytModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.65)',
    },
    ytModalSheet: {
      width: '100%',
      maxWidth: 720,
      borderRadius: 12,
      borderWidth: 1,
      overflow: 'hidden',
      zIndex: 2,
    },
    ytModalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    ytModalTitle: {
      flex: 1,
      marginRight: 12,
      fontSize: 17,
      fontWeight: '600',
    },
    ytModalClose: { padding: 4 },
    ytModalCloseText: { fontSize: 20, fontWeight: '600' },
    ytWebViewWrap: {
      width: '100%',
      minHeight: 220,
      height: 320,
      backgroundColor: '#000',
    },
    ytWebView: { flex: 1, backgroundColor: '#000' },
    ytModalImage: { width: '100%', height: '100%', backgroundColor: '#000' },
    lightboxRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.92)',
    },
    lightboxSheet: {
      zIndex: 2,
      alignItems: 'center',
      paddingHorizontal: 12,
    },
    lightboxClose: {
      alignSelf: 'flex-end',
      marginBottom: 8,
      padding: 8,
    },
    lightboxCloseText: { fontSize: 22, fontWeight: '600', color: '#fff' },
    lightboxRotateRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginTop: 16,
      gap: 24,
    },
    rotateHit: { padding: 8 },
    tagModalRoot: {
      flex: 1,
      justifyContent: 'center',
      paddingHorizontal: 20,
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    viewersSheet: {
      borderRadius: 12,
      borderWidth: 1,
      padding: 16,
      zIndex: 2,
    },
    viewersTitle: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 10,
    },
    viewerLine: {
      fontSize: 15,
      paddingVertical: 8,
    },
    modalDone: {
      marginTop: 12,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
    },
    modalDoneText: {
      fontSize: 16,
      fontWeight: '700',
      color: '#000',
    },
    filterOptionsSheet: {
      borderRadius: 12,
      borderWidth: 1,
      padding: 16,
      zIndex: 2,
      width: '100%',
      maxWidth: 400,
      alignSelf: 'center',
    },
    filterOptionsTitle: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 8,
    },
    filterOptionsScroll: {
      maxHeight: 320,
    },
    filterOptionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 14,
      paddingHorizontal: 4,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    filterOptionLabel: {
      flex: 1,
      fontSize: 16,
      lineHeight: 22,
      paddingRight: 12,
    },
  });
}
